import React, { useState, useEffect } from 'react';
import { Modal } from '../common/Modal';
import { Button } from '../common/Button';
import { LoadingSpinner } from '../common/LoadingSpinner';

export const EditWordDialog = ({ isOpen, word, onClose, onUpdate }) => {
  const [examples, setExamples] = useState([]);
  const [translation, setTranslation] = useState('');
  const [learningLevel, setLearningLevel] = useState('learning');
  const [isLoading, setIsLoading] = useState(false);
  const [regeneratingIndexes, setRegeneratingIndexes] = useState(new Set());
  const [error, setError] = useState('');

  const levelConfig = {
    mastered: {
      label: 'Mastered',
      bgColor: 'bg-green-100',
      textColor: 'text-green-800',
      borderColor: 'border-green-300',
    },
    learning: {
      label: 'Learning',
      bgColor: 'bg-blue-100',
      textColor: 'text-blue-800',
      borderColor: 'border-blue-300',
    },
    hard: {
      label: 'Hard',
      bgColor: 'bg-orange-100',
      textColor: 'text-orange-800',
      borderColor: 'border-orange-300',
    },
  };

  useEffect(() => {
    if (word) {
      setExamples([...word.examples]);
      setTranslation(word.translation?.text || '');
      setLearningLevel(word.learningLevel || 'learning');
    }
  }, [word]);

  const handleExampleChange = (index, value) => {
    const newExamples = [...examples];
    newExamples[index] = {
      ...newExamples[index],
      text: value,
      isManuallyEdited: true,
    };
    setExamples(newExamples);
  };

  const handleSave = async () => {
    // Validate all examples have content
    const hasEmptyExample = examples.some((ex) => !ex.text?.trim());

    if (hasEmptyExample) {
      setError('All examples must have content');
      return;
    }

    try {
      setIsLoading(true);
      setError('');

      // Extract text from examples
      const exampleTexts = examples.map((ex) => ex.text.trim());

      // Build update payload
      const updateData = {
        examples: exampleTexts,
        regenerate: false,
      };

      // Include learning level if changed
      if (learningLevel !== word.learningLevel) {
        updateData.learningLevel = learningLevel;
      }

      // Include translation if changed
      if (word.translation && translation !== word.translation.text) {
        updateData.translation = translation;
      }

      await onUpdate(word.wordId, updateData);

      onClose();
    } catch (err) {
      setError(err.message || 'Failed to update word');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRegenerateExample = async (exampleIndex) => {
    // Check limit
    const example = examples[exampleIndex];
    const regenCount = example.regenerationCount || 0;

    if (regenCount >= 3) {
      setError('Maximum regenerations reached for this example');
      return;
    }

    try {
      setRegeneratingIndexes(prev => new Set(prev).add(exampleIndex));
      setError('');

      const updated = await onUpdate(word.wordId, {
        regenerate: true,
        exampleIndex: exampleIndex,
      });

      // Update only the regenerated example, preserve other local edits
      setExamples(prevExamples => {
        const newExamples = [...prevExamples];
        newExamples[exampleIndex] = updated.examples[exampleIndex];
        return newExamples;
      });
    } catch (err) {
      setError(err.message || 'Failed to regenerate example');
    } finally {
      setRegeneratingIndexes(prev => {
        const newSet = new Set(prev);
        newSet.delete(exampleIndex);
        return newSet;
      });
    }
  };

  const getRemainingRegenerations = (example) => {
    const count = example.regenerationCount || 0;
    return Math.max(0, 3 - count);
  };

  const canRegenerateExample = (example) => {
    return getRemainingRegenerations(example) > 0;
  };

  const handleTranslationChange = (value) => {
    setTranslation(value);
  };

  const handleClose = () => {
    if (!isLoading && regeneratingIndexes.size === 0) {
      setError('');
      onClose();
    }
  };

  if (!word) return null;

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={`Edit: ${word.word}`} size="lg">
      <div className="space-y-4">
        {/* Learning Progress Section */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Learning Progress
          </label>
          <div className="flex gap-2">
            {Object.entries(levelConfig).map(([level, config]) => (
              <button
                key={level}
                type="button"
                onClick={() => setLearningLevel(level)}
                disabled={isLoading}
                className={`
                  flex-1 px-4 py-2 text-sm font-medium rounded-lg border transition-all
                  ${
                    learningLevel === level
                      ? `${config.bgColor} ${config.textColor} ${config.borderColor} border-2`
                      : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                  }
                  ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}
                `}
              >
                {config.label}
              </button>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Examples
          </label>
          {examples.map((example, index) => {
            const remaining = getRemainingRegenerations(example);
            const canRegenerate = canRegenerateExample(example);
            const isRegenerating = regeneratingIndexes.has(index);

            return (
              <div key={index} className="mb-4">
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-medium text-gray-600">
                    Example {index + 1}
                  </label>
                  <button
                    type="button"
                    onClick={() => handleRegenerateExample(index)}
                    disabled={!canRegenerate || isLoading || isRegenerating}
                    className={`
                      flex items-center gap-1 px-2 py-1 text-xs rounded
                      transition-colors duration-200
                      ${
                        canRegenerate
                          ? 'text-primary-600 hover:bg-primary-50 hover:text-primary-700'
                          : 'text-gray-400 cursor-not-allowed'
                      }
                      ${isRegenerating ? 'opacity-50' : ''}
                      disabled:cursor-not-allowed
                    `}
                    title={
                      canRegenerate
                        ? `Regenerate (${remaining} attempt${remaining !== 1 ? 's' : ''} remaining)`
                        : 'Maximum regenerations reached'
                    }
                  >
                    {isRegenerating ? (
                      <>
                        <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                            fill="none"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        <span>Regenerating...</span>
                      </>
                    ) : (
                      <>
                        <svg
                          className="w-3 h-3"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                          />
                        </svg>
                        <span>{canRegenerate ? `${remaining} left` : 'Max reached'}</span>
                      </>
                    )}
                  </button>
                </div>
                <textarea
                  value={example.text}
                  onChange={(e) => handleExampleChange(index, e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                  rows={2}
                  disabled={isLoading || isRegenerating}
                />
              </div>
            );
          })}
        </div>

        {/* Translation Section */}
        {word.translation && (
          <div className="border-t pt-4 mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Translation in {word.translation.targetLanguage?.toUpperCase() || 'Native Language'}
            </label>
            <input
              type="text"
              value={translation}
              onChange={(e) => handleTranslationChange(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
              disabled={isLoading}
              placeholder="Enter translation..."
            />
          </div>
        )}

        {error && <p className="text-sm text-red-500">{error}</p>}

        {isLoading && regeneratingIndexes.size === 0 && (
          <div className="py-4">
            <LoadingSpinner />
            <p className="text-center text-sm text-gray-600 mt-2">Updating...</p>
          </div>
        )}

        <div className="flex items-center justify-end gap-3 pt-4 border-t">
          <Button variant="secondary" onClick={handleClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isLoading || regeneratingIndexes.size > 0}>
            Save Changes
          </Button>
        </div>
      </div>
    </Modal>
  );
};
